/**
 * Computes aggregated summary statistics (insights) from fishery data for KeyInsights cards.
 * Used by StockDataProvider.
 */

export type InsightsResult = {
  exVesselValue?: number;
  exVesselUncertainty?: number;
  economyValue?: number;
  economyUncertainty?: number;
  missedEconomyValue?: number;
  missedEconomyUncertainty?: number;
  missedValue?: number;
  missedValueUncertainty?: number;
  missedWeight?: number;
  missedWeightUncertainty?: number;
  mealsProduced?: number;
  mealsUncertainty?: number;
  tradeDeficit?: number;
  tradeDeficitUncertainty?: number;
  totalJobs?: number;
  totalJobsUncertainty?: number;
  harvestingJobs?: number;
  processingJobs?: number;
  distributionJobs?: number;
  supportServicesJobs?: number;
  inducedJobs?: number;
  totalRevenue?: number;
  totalBaitConsumption?: number;
  avgProfit?: number;
  avgBaitCost?: number;
  avgHerringPrice?: number;
  avgMenhadenPrice?: number;
  avgWeightedBaitPrice?: number;
};

type FisheryRecord = Record<string, unknown>;

export function calculateInsights(
  fisheryData: FisheryRecord[],
  options: {
    forPeriod?: 'full' | 'historical' | 'projections' | null;
    selectedStockId: number | null;
    selectedModel: string;
    lobsterResults?: { results?: Array<{ fishery_revenue_millions?: number }>; annual_results?: FisheryRecord[] } | null;
  }
): InsightsResult | null {
  if (!fisheryData?.length) return null;

  const { forPeriod = 'full', selectedStockId, selectedModel, lobsterResults } = options;

  let filteredData: FisheryRecord[];
  if (forPeriod === 'historical') {
    filteredData = fisheryData.filter((item) => (item.year as number) <= 2024);
  } else if (forPeriod === 'projections') {
    filteredData = fisheryData.filter((item) => (item.year as number) >= 2025);
  } else {
    filteredData = [...fisheryData];
  }

  if (filteredData.length === 0) return null;

  const shouldAggregateAll = selectedStockId === null || selectedModel === 'CASA/SAMS';

  // Lobster: annual_results format
  if (selectedModel === 'LOBSTER' && lobsterResults?.annual_results) {
    const results = lobsterResults.annual_results;
    return {
      totalRevenue: results.reduce((sum, row) => sum + (Number(row.revenue) || 0), 0),
      totalBaitConsumption: results.reduce((sum, row) => sum + (Number(row.bait_consumption) || 0), 0),
      avgHerringPrice: results.reduce((sum, row) => sum + (Number(row.herring_price) || 0), 0) / results.length,
      avgMenhadenPrice: results.reduce((sum, row) => sum + (Number(row.menhaden_price) || 0), 0) / results.length,
      avgWeightedBaitPrice: results.reduce((sum, row) => sum + (Number(row.weighted_bait_price) || 0), 0) / results.length,
      avgProfit: results.reduce((sum, row) => sum + (Number(row.profit) || 0), 0) / results.length,
      avgBaitCost: results.reduce((sum, row) => sum + (Number(row.bait_cost) || 0), 0) / results.length,
    };
  }

  // Lobster: fisheryData format (from transformed lobster results)
  if (selectedModel === 'LOBSTER' && lobsterResults?.results) {
    const avgProfit = filteredData.reduce((sum, item) => sum + (Number(item.lobster_profit) || 0), 0) / (filteredData.length || 1);
    const avgBaitCost = filteredData.reduce((sum, item) => sum + (Number(item.lobster_bait_cost) || 0), 0) / (filteredData.length || 1);
    const totalRevenue = lobsterResults.results.reduce((sum, r) => sum + (Number(r.fishery_revenue_millions) || 0), 0) / 3;
    return {
      totalRevenue,
      totalBaitConsumption: filteredData.reduce((sum, item) => sum + (Number(item.lobster_bait_consumption) || 0), 0),
      avgProfit,
      avgBaitCost,
      avgHerringPrice: filteredData.reduce((sum, item) => sum + (Number(item.lobster_herring_price) || 0), 0) / (filteredData.length || 1),
      avgMenhadenPrice: filteredData.reduce((sum, item) => sum + (Number(item.lobster_menhaden_price) || 0), 0) / (filteredData.length || 1),
      avgWeightedBaitPrice: filteredData.reduce((sum, item) => sum + (Number(item.lobster_weighted_bait_price) || 0), 0) / (filteredData.length || 1),
    };
  }

  const sum = (arr: FisheryRecord[], key: string) =>
    arr.reduce((s, item) => s + (Number(item[key]) || 0), 0);
  const sumSq = (arr: FisheryRecord[], key: string) =>
    Math.sqrt(arr.reduce((s, item) => s + Math.pow(Number(item[key]) || 0, 2), 0));

  let data = shouldAggregateAll
    ? filteredData
    : filteredData.filter(
        (item) =>
          Number(item.stock_id) === Number(selectedStockId) ||
          Number(item.fishery_management_stock_id) === Number(selectedStockId)
      );
  // Fallback: API may return pre-filtered single-stock data without stock_id, or stock_id as string
  if (data.length === 0 && filteredData.length > 0) {
    const uniqueStockIds = [
      ...new Set(
        filteredData
          .map((i) => i.stock_id ?? i.fishery_management_stock_id)
          .filter((id) => id != null)
      ),
    ];
    // Use all data when: one stock in response, or no stock_ids (pre-filtered by UUID)
    if (uniqueStockIds.length === 1 || uniqueStockIds.length === 0) {
      data = filteredData;
    }
  }
  if (data.length === 0) return null;

  return {
    exVesselValue: sum(data, 'mean_ex_vessel'),
    exVesselUncertainty: sumSq(data, 'ex_vessel_uncertainty'),
    economyValue: sum(data, 'landed_economic_impact'),
    economyUncertainty: sumSq(data, 'landed_economic_impact_uncertainty'),
    missedEconomyValue: sum(data, 'uncaught_economic_impact'),
    missedEconomyUncertainty: sumSq(data, 'uncaught_economic_impact_uncertainty'),
    missedValue: sum(data, 'mean_uncaught_ex_vessel'),
    missedValueUncertainty: sumSq(data, 'uncaught_ex_vessel_uncertainty'),
    missedWeight: sum(data, 'mean_uncaught_acl'),
    missedWeightUncertainty: sumSq(data, 'uncaught_acl_uncertainty'),
    mealsProduced: sum(data, 'meals_produced'),
    mealsUncertainty: sumSq(data, 'meals_uncertainty'),
    tradeDeficit: sum(data, 'trade_deficit_contribution'),
    tradeDeficitUncertainty: sumSq(data, 'trade_deficit_uncertainty'),
    totalJobs: sum(data, 'total_jobs_mean'),
    totalJobsUncertainty: sumSq(data, 'total_jobs_uncertainty'),
    harvestingJobs: sum(data, 'harvesting_jobs_mean'),
    processingJobs: sum(data, 'processing_jobs_mean'),
    distributionJobs: sum(data, 'distribution_jobs_mean'),
    supportServicesJobs: sum(data, 'support_services_jobs_mean'),
    inducedJobs: sum(data, 'induced_jobs_mean'),
  };
}
